import { RecurrenceDaysOfTheWeek } from '@tableau/self-service-schedules/lib/src/components/Recurrence';
import { DialogWidget,DropdownButtonType, IntegerStepperWidget, List, ListItem, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, RadioLayoutType,
  SingleSelectDropdown,
  TextLinkKind,
  TextLinkWidget} from '@tableau/widgets';
import { ButtonType, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Set } from '~commonInfra/utils/Set';
import { dialogBodyStyle } from '~src/catalog/styles/ExternalContentActionsStyles';
import { Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/commonInfra/components/ButtonWithSpinner';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg, StandardActionToastMessages } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { ActionButtonLeftWide,ActionButtons } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { CachingPolicyType } from '~src/dataFreshness/models/IDataFreshnessInfo';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { IWorkbookBase, IWorkbookInfo } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { WorkbookErrors } from '~src/workbook/services/WorkbookErrors';

import { DataFreshnessPolicyConfigEditorProps, DataFreshnessPolicyDialogProps, DataFreshnessPolicyOption,
  DataFreshnessPolicyState, DataFreshnessRecurrenceType, MINUTES_PER_WEEK, TimeUnit, UpdateDataFreshnessPolicyFooterProps }
from '../types/DataFreshnessTypes';
import { findCachingPolicyType, findIntervalInMinutes as findIntervalInMinutes,
  findProperRefreshAfterWithUnit, findRefreshTimeOfDay, getDaysStringCsv, mapToRurrenceType, mapToRurrenceWeekDay } from '../utils/DataFreshnessUtils';
import { DataFreshnessRecurrenceEditor } from './DataFreshnessRecurrenceEditor';

export namespace UpdateDataFreshnessPolicyHelper {
  export const DataFreshnessPolicyConfigEditor = (props: DataFreshnessPolicyConfigEditorProps): JSX.Element => {
    const [dataFreshnessPolicySelectedIndex, setDataFreshnessPolicySelectedIndex] =
      React.useState(getSelectedDataFreshnessPolicyIndex.get(props.dialogState.selectedDataFreshnessPolicy));

    return <div style={{ marginTop: 5}} >
      <p>{Messages.editDataFreshnessPolicyDialogDescription()}
      <TextLinkWidget testId={'data-freshness-learn-more-link'}
                      target={'_blank'}
                      className={learnMoreStyle}
                      kind={TextLinkKind.Standard}
                      href={HelpService.getServerHelpUrlByCategory('freshness')}
                      >
        {Messages.learnMore()}
      </TextLinkWidget>
      </p>
      <RadioButtonWithLabelGroupWidget
          aria-label={Messages.editDataFreshnessPolicyDialogTitle()}
          buttonList={getDataFreshnessPolicyOptions(props)}
          handleChange={(value: string, checkedIndex: number)=> {
            setDataFreshnessPolicySelectedIndex(checkedIndex);
            props.dialogState.selectedDataFreshnessPolicy = value;
            props.validatePolicy(props.dialogState);
          }}
          checkedIndex={dataFreshnessPolicySelectedIndex}
          layout={RadioLayoutType.Veritical}
          testId={'data-freshness-policy-options'}
      />
      {props.dialogState.selectedDataFreshnessPolicy === DataFreshnessPolicyOption.FreshAt &&
        <DataFreshnessRecurrenceEditor {...props}/>
      }
    </div>;
  };

  export const UpdateDataFreshnessPolicyFooter = (props: UpdateDataFreshnessPolicyFooterProps): JSX.Element => {
    const [updatingDataFreshnessPolicy, setUpdatingDataFreshnessPolicy] = React.useState<boolean>(false);
    const {updateDataFreshnessPolicy, closeDialog} = props;

    const updateAction = React.useCallback(async () => {
      setUpdatingDataFreshnessPolicy(true);
      await updateDataFreshnessPolicy();
      closeDialog();
    }, [setUpdatingDataFreshnessPolicy, closeDialog, updateDataFreshnessPolicy]);

    return <div className={ActionButtons} >
      <ButtonWithSpinner
        buttonType={ButtonType.Outline}
        onClick={() => props.dismissDialog()}
        testId={'data-freshness-update-cancel'}
        children={Messages.cancel()}
        disabled={false}
      />
      <span className={ActionButtonLeftWide}>
        <ButtonWithSpinner
          buttonType={ButtonType.Primary}
          onClick={updateAction}
          testId={'data-freshness-update-confirm'}
          children={Messages.ok()}
          disabled={props.invalidPolicy}
          showSpinner={updatingDataFreshnessPolicy}
        />
      </span>
    </div>;
  };

  function workbookUsesDefaultPolicy(workbook: IWorkbookInfo): boolean {
    return workbook.workbookDataFreshness === undefined || workbook.workbookDataFreshness === null ||
            workbook.workbookDataFreshness.cachingPolicyType === undefined;
  }

  export function initializeDataFreshnessDialogState(workbook: IWorkbookInfo): DataFreshnessPolicyState {
    var initialSelection = DataFreshnessPolicyOption.FreshAt;
    if (workbookUsesDefaultPolicy(workbook)) {
      return {selectedDataFreshnessPolicy: DataFreshnessPolicyOption.Default,
        refreshAfterValue: workbook.workbookDataFreshness.defaultCacheTimeoutInMinutes / 60,
        refreshAfterTimeUnit: TimeUnit.Hours,
        refreshTimeOfDay: 0,
        refreshAtDaysOfMonth: [],
        refreshAtDaysOfWeek: [],
        refreshAtType: DataFreshnessRecurrenceType.Day
      };
    } else if (workbook.workbookDataFreshness.cachingPolicyType === CachingPolicyType.INTERVAL_IN_MINUTES) {
      initialSelection = workbook.workbookDataFreshness.intervalInMinutes === 0
        ? DataFreshnessPolicyOption.AlwaysLive : DataFreshnessPolicyOption.FreshAfter;
    }

    const [initValue, initTimeUnit] = findProperRefreshAfterWithUnit(workbook.workbookDataFreshness.defaultCacheTimeoutInMinutes,
      workbook.workbookDataFreshness.intervalInMinutes);

    const recurrentType = workbook.workbookDataFreshness.cachingPolicyType;

    const refreshTimeOfDay = workbook.workbookDataFreshness.refreshTimeOfDay;
    var daysOfWeek: RecurrenceDaysOfTheWeek[] = [];
    var daysOfMonth: number[] = [];
    workbook.workbookDataFreshness.daysStringCsv &&
    workbook.workbookDataFreshness.daysStringCsv.split(',').forEach((day: string) => {
      recurrentType === CachingPolicyType.REPEAT_MONTHLY && daysOfMonth.push(parseInt(day));
      recurrentType === CachingPolicyType.REPEAT_WEEKLY && daysOfWeek.push(mapToRurrenceWeekDay(day));
    });

    return {selectedDataFreshnessPolicy: initialSelection,
            refreshAfterValue: initValue,
            refreshAfterTimeUnit: initTimeUnit,
            refreshTimeOfDay: refreshTimeOfDay || 0,
            refreshAtDaysOfMonth: daysOfMonth,
            refreshAtDaysOfWeek: daysOfWeek,
            refreshAtType: mapToRurrenceType(recurrentType) || DataFreshnessRecurrenceType.Day,
            refreshAtTimezone: workbook.workbookDataFreshness.timezone
          };
  }

  function dialogContainsInvalidPolicy(dialogState: DataFreshnessPolicyState): boolean {
    if (dialogState === undefined) {
      return true;
    }

    if (dialogState.selectedDataFreshnessPolicy === undefined) {
      return true;
    }

    if (dialogState.selectedDataFreshnessPolicy !== DataFreshnessPolicyOption.FreshAt ||
      dialogState.refreshAtType === DataFreshnessRecurrenceType.Day) {
      return false;
    }

    return dialogState.refreshAtType === DataFreshnessRecurrenceType.Week &&
      (dialogState.refreshAtDaysOfWeek === undefined ||
      dialogState.refreshAtDaysOfWeek.length === 0) ||
      dialogState.refreshAtType === DataFreshnessRecurrenceType.Month &&
      (dialogState.refreshAtDaysOfMonth === undefined ||
      dialogState.refreshAtDaysOfMonth.length === 0);
  }

  export function DataFreshnessPolicyConfigDialog(props: DataFreshnessPolicyDialogProps): JSX.Element {
    const [invalidPolicy, setInvalidPolicy] = React.useState<boolean>(false);
    return <DialogWidget
      title= {Messages.editDataFreshnessPolicyDialogTitle()}
      testId={'data-freshness-update-dialog'}
      type= {DialogType.NormalWithTitleBar}
      showCloseButton={false}
      positioningBehavior={DialogPositioningBehavior.Centered}
      dialogBodyStyle= {dialogBodyStyle}
      dialogWidth= {TabDialogWidth.width500px}
      onCloseRequest={() => props.closeDialog()}
      isModal={true}
      disableAutoFocus={true}
      footerContent={<UpdateDataFreshnessPolicyFooter
        dismissDialog={() => props.dismissDialog()}
        closeDialog={() => props.closeDialog()}
        updateDataFreshnessPolicy={() => updateDataFreshnessPolicy(props.workbook, props.initDialogState)}
        invalidPolicy={invalidPolicy}/>}
      >
        <DataFreshnessPolicyConfigEditor
          workbook={props.workbook}
          actionBusy= {true}
          dialogState= {props.initDialogState}
          updateDialogState={null}
          doAction= {null}
          validatePolicy={(dialogState: DataFreshnessPolicyState) =>
              setInvalidPolicy(dialogContainsInvalidPolicy(dialogState))}/>
    </DialogWidget>;
  }

  export function confirmUpdateDataFreshnessPolicy(workbook: IWorkbookInfo) {
    const dialogState = initializeDataFreshnessDialogState(workbook);
    const modal = ReactModalService.openCustom(closeAndDismiss => {
      return <DataFreshnessPolicyConfigDialog
        workbook={workbook}
        initDialogState={dialogState}
        closeDialog={() => modal.close()}
        dismissDialog={() => modal.dismiss()}
        />;
    });
    return Promise.resolve(modal.result);
  }


  function updateDataFreshnessPolicy(workbook: IWorkbookInfo, dialogState: DataFreshnessPolicyState) {
    return Workbooks.updateDataFreshnessPolicy(workbook.id,
        dialogState.selectedDataFreshnessPolicy === DataFreshnessPolicyOption.Default,
        findIntervalInMinutes(dialogState),
        findCachingPolicyType(dialogState),
        getDaysStringCsv(dialogState),
        findRefreshTimeOfDay(dialogState),
        findTimezoneId(dialogState))
      .then((result: IResult) => {
        notifyResult(workbook, result);
      });
  }

  function findTimezoneId(dialogState: DataFreshnessPolicyState): string {
    return dialogState.selectedDataFreshnessPolicy === DataFreshnessPolicyOption.FreshAt
            ? dialogState.refreshAtTimezone && dialogState.refreshAtTimezone.timeZoneId : undefined;
  }

  // TODO: use detail messages for errors
  function notifyResult(workbook: IWorkbookBase, result: IResult) {
    const translations: StandardActionToastMessages =
      {
        success: standardActionSuccessMsg(Messages.editDataFreshnessPolicySuccess),
        error: standardActionErrorMsg(Messages.editDataFreshnessPolicyFailure),
      };

    ActionHelpers.displayStandardToastsForResult(
      Set.singletonForIdItem(workbook),
      result,
      translations,
      (errorItem: IErrorItem<IWorkbookBase>) => WorkbookErrors.errorDetailsFor(errorItem)
    );
  }

  // returns a widget with an integer stepper widget (time value) and a drop down (time unit)
  function SimpleDataFreshnessPolicy(props: DataFreshnessPolicyConfigEditorProps) {
    const disabled = props.dialogState.selectedDataFreshnessPolicy !== DataFreshnessPolicyOption.FreshAfter;

    const [initValue, initTimeUnit] = findProperRefreshAfterWithUnit(props.workbook.workbookDataFreshness.defaultCacheTimeoutInMinutes,
      props.workbook.workbookDataFreshness.intervalInMinutes);
    props.dialogState.refreshAfterValue = props.dialogState.refreshAfterValue || initValue;
    props.dialogState.refreshAfterTimeUnit = props.dialogState.refreshAfterTimeUnit || initTimeUnit;

    const [timeUnit, setTimeUnit] = React.useState(initTimeUnit);
    const listItems = getTimeUnitItems();
    return <>
    <div className={style({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' })}>
      <IntegerStepperWidget
      min={1}
      max={MINUTES_PER_WEEK}
      start={props.dialogState.refreshAfterValue}
      testId={'data-freshness-refresh-after-value'}
      disabled={disabled}
      onValueUpdated={(value: number) => props.dialogState.refreshAfterValue = value}
      />
    <SingleSelectDropdown
      testId={'data-freshness-timeunit-dropdown'}
      list={<List items={listItems}  />}
      type={DropdownButtonType.Outline}
      label=''
      value={timeUnit}
      disabled={disabled}
      onChange={(timeUnit: TimeUnit) => {
        props.dialogState.refreshAfterTimeUnit = timeUnit;
        setTimeUnit(timeUnit);
      }}
    />
  </div>
    </>;
  }

  function getTimeUnitItems(): ListItem[] {
    return [
    {value: TimeUnit.Minutes, content: Messages.timeUnitMinutes(), testId: 'data-freshness-time-unit-minutes'},
    {value: TimeUnit.Hours, content: Messages.timeUnitHours(), testId: 'data-freshness-time-unit-hours'},
    {value: TimeUnit.Days, content: Messages.timeUnitDays(), testId: 'data-freshness-time-unit-days'},
    {value: TimeUnit.Weeks, content: Messages.timeUnitWeeks(), testId: 'data-freshness-time-unit-weeks'}];
  }

  function getDataFreshnessPolicyOptions(props: DataFreshnessPolicyConfigEditorProps): RadioButtonWithLabelOption<string>[] {
    return [
      {
        value: DataFreshnessPolicyOption.Default,
        testId: `data-freshness-radio-button-default`,
        label: Messages.editDataFreshnessPolicyDialog_RadioButton_Default(
          { DefaultDataFreshnessInHour: (props.workbook.workbookDataFreshness.defaultCacheTimeoutInMinutes / 60).toString() })
      },
      {
        value: DataFreshnessPolicyOption.AlwaysLive,
        testId: `data-freshness-radio-button-disabled`,
        label: Messages.editDataFreshnessPolicyDialog_RadioButton_AlwaysLive()
      },
      {
        value: DataFreshnessPolicyOption.FreshAfter,
        testId: `data-freshness-radio-button-fresh-after`,
        label: <p style={{ marginBlockStart: 0, marginBlockEnd: 0, marginRight: 5 }}>{Messages.editDataFreshnessPolicyDialog_RadioButton_RefreshAfter()}</p>,
        detail: <SimpleDataFreshnessPolicy {...props} />
      },
      {
        value: DataFreshnessPolicyOption.FreshAt,
        testId: `data-freshness-radio-button-fresh-at`,
        label: Messages.editDataFreshnessPolicyDialog_RadioButton_RefreshAt(),
      }
    ];
  }

  const getSelectedDataFreshnessPolicyIndex = new Map<string, number>([
    [DataFreshnessPolicyOption.Default, 0],
    [DataFreshnessPolicyOption.AlwaysLive, 1],
    [DataFreshnessPolicyOption.FreshAfter, 2],
    [DataFreshnessPolicyOption.FreshAt, 3],
  ]);
}

export type UpdateDataFreshnessPolicyHelper = typeof UpdateDataFreshnessPolicyHelper;

